
import {
  Component, Prop, Watch,
} from 'vue-property-decorator';
import Vue from 'vue';
import {
  RuleCustomValue, RuleCondition, RuleAction,
  RuleConditionDefinition, RuleActionDefinition,
} from '@/types';
import { applyDefaultSender, isCustomValueComplete } from '@/helpers/ruleHelpers';
import { matchesAllWords } from '@/helpers/textSearch';
import RuleCustomValueEditor from './RuleCustomValueEditor.vue';

@Component({
  components: { RuleCustomValueEditor },
})
export default class RuleItemPickerDialog extends Vue {
  @Prop({ type: Boolean, required: true }) readonly value!: boolean;
  @Prop({ type: Array, required: true }) readonly definitions!: (RuleConditionDefinition | RuleActionDefinition)[];
  @Prop({ type: String, required: true }) readonly type!: 'condition' | 'action';
  @Prop({ default: null }) readonly editItem!: RuleCondition | RuleAction | null;
  @Prop({ type: Array, default: () => [] }) readonly users!: any[];
  @Prop({ type: Array, default: () => [] }) readonly groups!: any[];
  @Prop({ type: Array, default: () => [] }) readonly categories!: any[];
  @Prop({ type: Array, default: () => [] }) readonly tags!: any[];
  @Prop({ type: Array, default: () => [] }) readonly templates!: any[];
  @Prop({ type: Array, default: () => [] }) readonly checklists!: any[];
  @Prop({ type: Boolean, default: false }) readonly hideBlocksTab!: boolean;

  searchQuery = '';
  activeTab = 0;
  selectedDefinition: RuleConditionDefinition | RuleActionDefinition | null = null;
  formCustomValues: RuleCustomValue[] = [];

  get blockOptions() {
    return [
      {
        operator: 'or', name: 'Oppure', description: 'Verifica se almeno una condizione è vera', icon: 'mdi-gate-or',
      },
      {
        operator: 'and', name: 'E', description: 'Verifica se tutte le condizioni sono vere', icon: 'mdi-gate-and',
      },
      {
        operator: 'not', name: 'Non', description: 'Verifica se la condizione è falsa', icon: 'mdi-gate-not',
      },
    ];
  }

  get isEditMode(): boolean {
    return this.editItem !== null;
  }

  get dialogTitle(): string {
    const itemLabel = this.type === 'condition' ? 'condizione' : 'azione';
    return this.isEditMode ? `Modifica ${itemLabel}` : `Aggiungi ${itemLabel}`;
  }

  get filteredDefinitions(): (RuleConditionDefinition | RuleActionDefinition)[] {
    // Le obsolete non si propongono: resta visibile solo quella già scelta,
    // quando si modifica una condizione o un'azione che la usa
    const currentCodename = this.editItem ? this.editItem.codename : null;
    return this.definitions
      .filter((d) => !d.obsolete || d.codename === currentCodename)
      .filter((d) => matchesAllWords(this.searchQuery, [d.name]));
  }

  get isFormComplete(): boolean {
    if (!this.selectedDefinition) return false;
    if (this.formCustomValues.length === 0) return true;
    return this.formCustomValues.every((cv) => isCustomValueComplete(cv));
  }

  @Watch('value')
  onDialogOpen(open: boolean): void {
    if (open) {
      this.searchQuery = '';
      this.activeTab = 0;

      if (this.editItem) {
        this.selectedDefinition = this.definitions.find(
          (d) => d.codename === this.editItem!.codename,
        ) || null;
        this.formCustomValues = this.editItem!.customValues.map(
          (cv) => JSON.parse(JSON.stringify(cv)),
        );
      } else {
        this.selectedDefinition = null;
        this.formCustomValues = [];
        // Auto-focus search field in add mode (delay for dialog transition)
        setTimeout(() => {
          const field = this.$refs.searchField as any;
          if (field) field.focus();
        }, 300);
      }
    }
  }

  selectDefinition(def: RuleConditionDefinition | RuleActionDefinition): void {
    this.selectedDefinition = def;
    this.formCustomValues = def.customValues.map((cv) => {
      const cloned: RuleCustomValue = JSON.parse(JSON.stringify(cv));
      if (cloned.type === 'boolean') {
        cloned.booleanValue = cloned.booleanValue !== undefined ? cloned.booleanValue : false;
      } else {
        cloned.value = cloned.value !== undefined ? cloned.value : null;
      }
      return cloned;
    });
    // Il mittente e' obbligatorio: proposto subito, altrimenti "Aggiungi"
    // resta disabilitato finche' non lo si scrive a mano
    if (this.type === 'action') applyDefaultSender(this.formCustomValues, true);
  }

  onCustomValueChanged(idx: number, updatedCv: RuleCustomValue): void {
    Vue.set(this.formCustomValues, idx, updatedCv);
  }

  confirm(): void {
    if (!this.selectedDefinition) return;

    const item: any = {
      codename: this.selectedDefinition.codename,
      name: this.selectedDefinition.name,
      customValues: JSON.parse(JSON.stringify(this.formCustomValues)),
    };
    if (this.type === 'action') {
      item.order = 0;
    }

    if (this.isEditMode) {
      this.$emit('update', item);
    } else {
      this.$emit('add', item);
    }
    this.close();
  }

  addBlock(operator: string): void {
    this.$emit('add-block', { block: operator, conditions: [] });
    this.close();
  }

  close(): void {
    this.$emit('input', false);
  }
}
